import { useTranslation } from 'react-i18next';
import TenantPage from 'components/common/TenantPage';
import { DataGridPremium, GridColDef, GridToolbarExport, itIT } from '@mui/x-data-grid-premium';
import { useEffect, useState } from 'react';
import { Button, ButtonGroup } from '@mui/material';
import { Link } from 'react-router-dom';
import { logService } from 'services/api/LogService';
import { IOption, IVersion } from 'interfaces';
import LogsFilterForm from './components/LogsFilterForm';
import { Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { VersionsColumns } from 'components/common/enhanced/common-headers/versions';
import VersionDialog from './VersionDialog';
import AddIcon from '@mui/icons-material/Add';
import {
  additionalEquipmentCostService,
  categoryService,
  contractService,
  costElementService,
  equipmentInvoiceService,
  equipmentService,
  examService,
  invoiceService,
  laboratoryService,
  locationService,
  movementReasonService,
  movementService,
  operationalUnitService,
  orderService,
  productService,
  productWarehouseService,
  regionalRateService,
  sectorService,
  supplierService,
  ticketingService,
  transportDocumentService,
  typeService,
  warehouseService,
  workstationService
} from 'services/api';
import { BaseService } from 'services/api/BaseService';
import { periodicCostElementService } from 'services/api/PeriodicCostElementService';

export const MODELS_MAPS: Record<
  string,
  {
    label: string;
    fields?: any[];
    service?: BaseService<any>;
    getMethod?: string;
    optionLabelField?: string;
  }
> = {
  product: {
    label: 'Articolo',
    service: productService
  },
  category: {
    label: 'Categoria',
    service: categoryService
  },
  transportdocument: {
    label: 'Consegna',
    service: transportDocumentService,
    optionLabelField: 'ref'
  },

  invoice: {
    label: 'Fattura',
    service: invoiceService,
    getMethod: 'getAllPaginated',
    optionLabelField: 'ref'
  },
  equipmentinvoice: {
    label: 'Fatturazione strumento',
    service: equipmentInvoiceService,
    getMethod: 'getAllPaginated',
    optionLabelField: 'ref'
  },
  movement: {
    label: 'Movimento',
    service: movementService,
    getMethod: 'getAllPaginated'
  },
  movementtype: {
    label: 'Causale',
    service: movementReasonService,
    getMethod: 'getAllPaginated'
  },
  warehouse: { label: 'Magazzino', service: warehouseService, optionLabelField: 'code' },
  contract: { label: 'Contratto', service: contractService, optionLabelField: 'code' },
  location: { label: 'Piazza', service: locationService },
  contractline: {
    label: 'Linea contratto',
    fields: [
      { name: 'Articolo', code: 'product', service: productService },
      {
        name: 'Contratto',
        code: 'contract_header',
        service: contractService,
        optionLabelField: 'code'
      }
    ]
  },
  contractequipment: {
    label: 'Contratto strumento',
    fields: [
      { name: 'Strumento', code: 'equipment', service: equipmentService },
      {
        name: 'Contratto',
        code: 'contract_header',
        service: contractService,
        optionLabelField: 'code'
      }
    ]
  },
  operationalunit: {
    label: 'Unità operativa',
    service: operationalUnitService
  },
  equipment: { label: 'Strumento', service: equipmentService, optionLabelField: 'code' },
  sector: { label: 'Settore', service: sectorService },
  supplier: { label: 'Fornitore', service: supplierService },
  type: { label: 'Tipo', service: typeService },
  periodiccostelement: {
    label: 'Costo Periodico',
    service: periodicCostElementService,
    getMethod: 'getAllPaginated'
  },
  costelement: {
    label: 'Costo Elemento',
    service: costElementService,
    getMethod: 'getAllPaginated'
  },
  additionalequipmentcost: {
    label: 'Costo Servizio Extra',
    service: additionalEquipmentCostService,
    getMethod: 'getAllPaginated',
    optionLabelField: 'code'
  },
  regionalrate: {
    label: 'Tariffa Regionale',
    service: regionalRateService
  },
  technicalassistenceticket: {
    label: 'Ticket Assistenza Tecnica',
    service: ticketingService,
    optionLabelField: 'id',
    getMethod: 'getAllPaginated'
  },
  workstation: {
    label: 'Stazione di lavoro',
    service: workstationService
  },
  exam: { label: 'Esame', service: examService },
  laboratory: { label: 'Laboratorio', service: laboratoryService },
  productwarehouse: {
    label: 'Soglia',
    fields: [
      { name: 'Articolo', code: 'product', service: productService },
      { name: 'Magazzino', code: 'warehouse', service: warehouseService }
    ],
    service: productWarehouseService
  },
  purchaseorderhead: {
    label: 'Ordine',
    service: orderService,
    getMethod: 'getAllPaginated',
    optionLabelField: 'ref'
  },
  purchaseorderline: {
    label: 'Linea ordine',
    fields: [
      {
        name: 'Ordine',
        code: 'ref_id',
        service: orderService,
        getMethod: 'getAllPaginated',
        optionLabelField: 'ref'
      },
      { name: 'Articolo', code: 'product', service: productService }
    ]
  }
};

export const Versions = () => {
  const translationPrefix = 'pages.versions';
  const [models, setModels] = useState<IOption[]>([]);
  const [versions, setVersions] = useState<IVersion[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [loading, setLoading] = useState(false);

  const [activeQuery, setActiveQuery] = useState<
    | { mode: 'model'; model: string; object: string; fields: Record<string, string> }
    | { mode: 'actor'; actor_name: string }
    | null
  >(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<any>(null);
  const [isFiltersAccordionOpen, setIsFiltersAccordionOpen] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    logService.getModels().then((res) => {
      setModels(
        res.map((item, index) => ({
          id: index,
          code: item.model,
          label: MODELS_MAPS[item.model]?.label || item.model
        }))
      );
    });
  }, []);

  const fetchPage = async (query: any, page: number, pageSize: number) => {
    if (!query) return;
    setLoading(true);
    try {
      const limit = pageSize;
      const offset = page * pageSize;
      let res: any;
      if (query.mode === 'model') {
        res = await logService.getVersions(query.model, query.object, query.fields, {
          limit,
          offset
        });
      } else {
        res = await logService.getVersionsByActor(query.actor_name, { limit, offset });
      }

      const rows = (res.logs || []).map((item) => ({
        ...item,
        // IMPORTANT: DataGrid row id must match backend detail endpoint `/auditlogs/<id>/`
        id: item.id
      }));
      setVersions(rows as any);
      setRowCount(Number(res.count ?? rows.length));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeQuery) return;
    fetchPage(activeQuery, paginationModel.page, paginationModel.pageSize);
  }, [activeQuery, paginationModel.page, paginationModel.pageSize]);

  const handleOpenDialog = (id: string) => {
    const version = versions.find((v) => v.id === Number(id));
    setSelectedVersion(version);
    setDialogOpen(true);
  };

  const handleFilter = async (values) => {
    setVersions([]);

    const fields = Object.entries(values)
      .filter(([key]) => key !== 'model' && key !== 'object' && key !== 'actor_name')
      .reduce((acc, [key, value]) => {
        acc[key] = value;
        return acc;
      }, {});

    const query = { mode: 'model' as const, model: values.model, object: values.object, fields };
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setActiveQuery(query);
    setIsFiltersAccordionOpen(false);
  };

  const toggleFiltersAccordion = () => {
    setIsFiltersAccordionOpen((prev) => !prev);
  };

  const handleActorSearch = (actorName: string) => {
    // Chiude eventuali dettagli aperti mentre si ricaricano i risultati.
    setSelectedVersion(null);
    setDialogOpen(false);
    setVersions([]);
    if (!actorName) {
      setActiveQuery(null);
      setRowCount(0);
      return;
    }
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    setActiveQuery({ mode: 'actor' as const, actor_name: actorName });
    setIsFiltersAccordionOpen(false);
  };
  const columns: GridColDef[] = VersionsColumns(handleOpenDialog);

  return (
    <>
      <TenantPage
        title={t(`${translationPrefix}.title`)}
        subtitle={t(`${translationPrefix}.subtitle`)}
        menuRight={
          <div className="flex justify-end items-center">
            <ButtonGroup>
              <Link to="deleted">
                <Button color="primary" size="small">
                  {t(`${translationPrefix}.delete-versions`)}
                </Button>
              </Link>
              <Link to="movement">
                <Button color="primary" size="small">
                  {t(`${translationPrefix}.movement-versions`)}
                </Button>
              </Link>
              <Link to="logging">
                <Button color="primary" size="small">
                  {t(`${translationPrefix}.sap_sync_logging`)}
                </Button>
              </Link>
              <Link to="access-logging">
                <Button color="primary" size="small">
                  {t(`${translationPrefix}.access-logging`)}
                </Button>
              </Link>
            </ButtonGroup>
          </div>
        }>
        <div className="mt-6">
          <Accordion
            className="bg-slate-50 my-8"
            onChange={toggleFiltersAccordion}
            expanded={isFiltersAccordionOpen}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="primary">
                <b>Filtri</b>
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <LogsFilterForm
                models={models}
                onSubmit={handleFilter}
                onActorSearch={handleActorSearch}
              />
            </AccordionDetails>
          </Accordion>
          {/* Item list */}
          <div>
            <DataGridPremium
              density="compact"
              rows={versions}
              loading={loading}
              localeText={itIT.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 'none', height: '75vh' }}
              disableSelectionOnClick
              pagination
              paginationMode="server"
              rowCount={rowCount}
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              onPageChange={(page) => setPaginationModel((prev) => ({ ...prev, page }))}
              onPageSizeChange={(pageSize) =>
                setPaginationModel((prev) => ({ ...prev, page: 0, pageSize }))
              }
              components={{ Toolbar: GridToolbarExport }}
              columns={columns}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </div>
        </div>

        {selectedVersion && (
          <VersionDialog
            open={dialogOpen}
            onClose={() => {
              setSelectedVersion(null);
              setDialogOpen(false);
            }}
            versionId={selectedVersion?.id}
          />
        )}
      </TenantPage>
    </>
  );
};

export default Versions;
